import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { indexer, lireRecents, ajouterRecent } from '../lib/lieux';
import { useAuth } from './useAuth';

// Source unique des lieux : chargés une seule fois, partagés par toutes les pages
// (accueil, liste, carte, favoris), puis mis à jour localement après chaque action.
const LieuxContext = createContext(null);

export const LieuxProvider = ({ children }) => {
  const { user, logout } = useAuth();
  const [lieux, setLieux] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recents, setRecents] = useState(lireRecents);

  const charger = useCallback(async () => {
    setLoading(true);
    setError('');
    const [data, cfg] = await Promise.all([api.getAdresses(), api.getConfig()]);
    if (data.status === 401) { logout(); return; }
    if (data.error) setError(data.error);
    else setLieux(data);
    if (!cfg.error) setConfig(cfg);
    setLoading(false);
  }, [logout]);

  useEffect(() => {
    if (user) charger();
    else { setLieux([]); setConfig(null); }
  }, [user, charger]);

  const indexes = useMemo(() => lieux.map(lieu => ({ lieu, index: indexer(lieu) })), [lieux]);

  const remplacer = (maj) => setLieux(ls => ls.map(l => (l.id === maj.id ? { ...l, ...maj } : l)));

  const creer = useCallback(async (donnees) => {
    const res = await api.createAdresse(donnees);
    if (!res.error) {
      setLieux(ls => [res, ...ls]);
      setConfig(c => (c ? { ...c, total: c.total + 1 } : c));
    }
    return res;
  }, []);

  const modifier = useCallback(async (id, donnees) => {
    const res = await api.updateAdresse(id, donnees);
    if (!res.error) remplacer(res);
    return res;
  }, []);

  const basculerFavori = useCallback(async (lieu) => {
    remplacer({ id: lieu.id, favori: !lieu.favori }); // optimiste
    const res = await api.updateAdresse(lieu.id, { favori: !lieu.favori });
    if (res.error) remplacer({ id: lieu.id, favori: lieu.favori });
    return res;
  }, []);

  const definirPrincipal = useCallback(async (lieu) => {
    const res = await api.setPrincipal(lieu.id);
    if (!res.error) setLieux(ls => ls.map(l => ({ ...l, principal: l.id === lieu.id })));
    return res;
  }, []);

  const supprimer = useCallback(async (id) => {
    const res = await api.deleteAdresse(id);
    if (!res.error) {
      setLieux(ls => ls.filter(l => l.id !== id));
      setConfig(c => (c ? { ...c, total: Math.max(0, c.total - 1) } : c));
      setRecents(r => r.filter(x => x !== id));
    }
    return res;
  }, []);

  const marquerConsulte = useCallback((id) => {
    ajouterRecent(id);
    setRecents(lireRecents());
  }, []);

  const parId = useMemo(() => Object.fromEntries(lieux.map(l => [l.id, l])), [lieux]);
  const lieuxRecents = useMemo(() => recents.map(id => parId[id]).filter(Boolean), [recents, parId]);
  const favoris = useMemo(() => lieux.filter(l => l.favori), [lieux]);
  const principal = useMemo(() => lieux.find(l => l.principal) || null, [lieux]);

  const valeur = {
    lieux, indexes, parId, favoris, principal, lieuxRecents, config, loading, error,
    charger, creer, modifier, basculerFavori, definirPrincipal, supprimer, marquerConsulte,
  };
  return <LieuxContext.Provider value={valeur}>{children}</LieuxContext.Provider>;
};

export const useLieux = () => useContext(LieuxContext);
